import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../hive/ui/Icon';
import { useFairTracker } from '../data/FairTrackerStore';
import { Empty, Pill, Stars, Thumb, useToast } from '../ui';
import {
  landed,
  mainPhoto,
  money,
  priceLine,
  shortlistMode,
  supplierDecision,
  supplierRating,
} from '../rules';
import ReportViewer from '../reports/ReportViewer';
import { p } from '../routes';
import { tr } from '../i18n';

/**
 * Final Shortlist (spec § 5.11).
 *
 * What this screen says changes with the fair's stage (rule S2): while the fair
 * is running it is a holding list, and once the fair is marked complete it
 * becomes the entry point to Final Review and starts showing landed cost. That
 * switch is the hinge between stage 1 and stage 2 of the whole app.
 */
export default function ShortlistScreen() {
  const toast = useToast();
  const { fair, visibleSuppliers, saveSupplier } = useFairTracker();

  const [report, setReport] = useState(false);

  const mode = shortlistMode(fair);
  const finalists = visibleSuppliers.filter((s) => s.inShortlist);
  const maybes = visibleSuppliers.filter((s) => !s.inShortlist && supplierDecision(s) === 'Maybe');

  const addMaybe = async (supplier) => {
    try {
      await saveSupplier({ ...supplier, manualShortlist: true, inShortlist: true });
      toast('Added to the shortlist');
    } catch (e) {
      toast('Could not save', 'error');
    }
  };

  return (
    <div className="ft-page">
      <div className="ft-title">
        <h1 className="ft-h1">{tr("Final Shortlist")}</h1>
        <p className="ft-sub" style={{ margin: 0 }}>
          {fair ? fair.name : ''} · {tr(`${finalists.length} finalist${finalists.length === 1 ? '' : 's'}`)}
        </p>
      </div>

      {/* Rule S2 — the banner is a dark hero once the fair is complete. */}
      {mode.complete ? (
        <section className="ft-hero">
          <h2 className="ft-hero-h1" style={{ fontSize: 21 }}>
            {tr(mode.banner)}
          </h2>
          <p className="ft-hero-lead">{tr(mode.lead)}</p>
        </section>
      ) : (
        <section className="ft-card">
          <h2 className="ft-h2">{tr(mode.banner)}</h2>
          <p className="ft-sub" style={{ margin: '4px 0 0' }}>
            {tr(mode.lead)}
          </p>
        </section>
      )}

      {!finalists.length && (
        <Empty title={tr("No finalists yet")} action={
          <Link className="ft-btn ft-btn-primary" to={p('/entry')}>{tr("Capture a supplier")}</Link>
        }>
          {tr("A supplier joins this list as soon as one of its products is marked Strong.")}
        </Empty>
      )}

      <div className="ft-list" style={{ marginTop: 12 }}>
        {finalists.map((s) => {
          const product = (s.products || []).find((x) => x.decision === 'Strong') || (s.products || [])[0];
          const l = landed(s);
          return (
            <article className="ft-supcard" key={s.id}>
              <Thumb photo={mainPhoto(s, product && product.id)} initials={s.company} size={56} />
              <div className="ft-supcard-main">
                <div className="ft-supcard-title">{s.company}</div>
                <div className="ft-supcard-meta">
                  {[product && product.name, product && priceLine(product, fair)].filter(Boolean).join(' · ')}
                </div>
                {mode.showLanded && (
                  <div className="ft-supcard-price">
                    {tr('Landed / unit:')} {l.perUnit != null ? money(l.perUnit) : tr('not calculated yet')}
                  </div>
                )}
                <div className="ft-supcard-pills">
                  <Pill>{supplierDecision(s)}</Pill>
                  {supplierRating(s) > 0 && <Stars value={supplierRating(s)} readOnly />}
                  {s.decision && <Pill>{s.decision}</Pill>}
                  {s.manualShortlist && <Pill tone="info">{tr("added manually")}</Pill>}
                </div>
              </div>
              <Link
                className="ft-btn ft-btn-sm ft-btn-primary"
                to={mode.complete ? p(`/review/${s.id}`) : p(`/s/${s.id}`)}
              >
                {tr(mode.buttonLabel)}
              </Link>
            </article>
          );
        })}
      </div>

      {finalists.length > 0 && (
        <div className="ft-btnrow" style={{ marginTop: 14 }}>
          <Link className="ft-btn ft-btn-ink" to={p('/outreach')}>
            <Icon name="MessageCircle" size={16} /> {tr("Message all finalists")}
          </Link>
          {finalists.length >= 2 && (
            <Link
              className="ft-btn"
              to={`${p('/compare')}?ids=${finalists.slice(0, 5).map((s) => s.id).join(',')}`}
            >
              {tr("Compare suppliers")}
            </Link>
          )}
          <button type="button" className="ft-btn" onClick={() => setReport(true)}>
            <Icon name="Printer" size={16} /> {tr("Shortlist report")}
          </button>
        </div>
      )}

      {maybes.length > 0 && (
        <section style={{ marginTop: 18 }}>
          <h2 className="ft-h2 ft-sectionhead">{tr("Add a Maybe to the shortlist")}</h2>
          <p className="ft-sub">
            {tr('A Maybe does not qualify automatically. Adding one here keeps it on the list even if it never becomes Strong (rule S1).')}
          </p>
          <div className="ft-list ft-list-rows">
            {maybes.map((s) => {
              const product = (s.products || [])[0];
              return (
                <div className="ft-rowcard" key={s.id}>
                  <Thumb photo={mainPhoto(s, product && product.id)} initials={s.company} size={40} />
                  <div className="ft-rowcard-main">
                    <b>{s.company}</b>
                    <div className="ft-faint">
                      {[product && product.name, product && priceLine(product, fair)]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  </div>
                  <button type="button" className="ft-btn ft-btn-sm" onClick={() => addMaybe(s)}>
                    {tr("Add")}
                  </button>
                </div>
              );
            })}
          </div>
        </section>
      )}
      {report && (
        <ReportViewer
          kind="shortlist"
          suppliers={visibleSuppliers}
          fair={fair}
          onClose={() => setReport(false)}
        />
      )}
    </div>
  );
}
